import React, {FC, useCallback, useEffect, useMemo, useRef, WheelEvent} from 'react';
import styles from './VideoPlayer.module.css';
import {useAppDispatch, useAppSelector} from "../../hooks";
import {activeVideoActions} from "../../state-slices/ActiveVideo";
import {Navigate} from "react-router-dom";
import {assert} from '../../assert';
import LocRecord from "../../data-structures/LocRecord";
import GpsRecord from "../../data-structures/GpsRecord";
import {getFullUrlFromRelative} from "../../GetFullUrlFromRelative";
import {formatFrameUrl} from "../../FormatFrameUrl";
import AssetRecord from "../../data-structures/AssetRecord";
import { debounce } from 'lodash';
import RulerOverlay from "./RulerOverlay/RulerOverlay";

interface VideoPlayerProps {
}

const VideoPlayer: FC<VideoPlayerProps> = () => {
    const shouldPlay = useAppSelector(state => state.activeVideoSlice.desiredPlaybackState);
    const shouldShowFramePreview= useAppSelector(state => state.activeVideoSlice.shouldShowFramePreview);
    const shouldLoadFramePreview = useAppSelector(state => state.activeVideoSlice.shouldLoadFramePreview);
    const shouldShowVideo = useAppSelector(state => state.activeVideoSlice.shouldShowMainVideo);
    const desiredVideoTime = useAppSelector(state => state.activeVideoSlice.desiredVideoTime);
    const previewFrameNumber = useAppSelector(state => state.activeVideoSlice.currentFrameToShowInPreview);
    const currentProject = useAppSelector(state => state.activeVideoSlice.projectState.currentProject);
    const currentClipRes = useAppSelector(state => state.activeVideoSlice.projectState.selectedResolution);
    const currentClip = useAppSelector(state => state.activeVideoSlice.projectState.selectedClip);
    const useHighRes = useAppSelector(state => state.activeVideoSlice.shouldUseHighResPreview);
    const framesToCache = useAppSelector(state => state.activeVideoSlice.framesToCache);
    const currentFrame = useAppSelector(state => state.activeVideoSlice.currentFrame);
    const shouldShowOverlayImage = useAppSelector(state => state.activeVideoSlice.shouldShowGrid);
    const shouldShowRuler = useAppSelector(state => state.activeVideoSlice.shouldShowRuler);
    const videoElement = useRef<HTMLVideoElement>(null);
    const lastSetTimeRef = useRef<number | null>(null);
    const dispatch = useAppDispatch();
    const locUrl = useAppSelector(state => state.activeVideoSlice.projectState.selectedRun?.locFile)
    const gpsUrl = useAppSelector(state => state.activeVideoSlice.projectState.selectedRun?.gpsFile)
    const assetsUrl = useAppSelector(state => state.activeVideoSlice.projectState.selectedRun?.assetsFile)
    const overlayImageRelativePath = useAppSelector(state => state.activeVideoSlice.projectState.selectedRun?.gridImageFile)
    assert(currentProject);

    // Add a time setting attempt counter for debugging
    const timeSetAttemptsRef = useRef(0);

    useEffect(() => {
        if (!videoElement.current || desiredVideoTime === null) {
            return;
        }

        // Avoid setting the same time multiple times
        if (lastSetTimeRef.current === desiredVideoTime) {
            return;
        }

        const video = videoElement.current;
        timeSetAttemptsRef.current++;
        
        // Log the attempt and current state
        console.log('Time set attempt:', {
            attempt: timeSetAttemptsRef.current,
            desiredTime: desiredVideoTime,
            currentTime: video.currentTime,
            readyState: video.readyState,
            paused: video.paused,
            seeking: video.seeking,
            duration: video.duration
        });

        // Only set time if video is properly loaded
        if (video.readyState >= 2) {
            try {
                video.currentTime = desiredVideoTime;
                lastSetTimeRef.current = desiredVideoTime;
                console.log('Time set successfully to:', desiredVideoTime);
            } catch (error) {
                console.error('Error setting video time:', error);
            }
        } else {
            // Add event listener for when video is loaded enough
            const handleCanPlay = () => {
                try {
                    video.currentTime = desiredVideoTime;
                    lastSetTimeRef.current = desiredVideoTime;
                    console.log('Time set after load to:', desiredVideoTime);
                } catch (error) {
                    console.error('Error setting video time after load:', error);
                }
                video.removeEventListener('canplay', handleCanPlay);
            };
            video.addEventListener('canplay', handleCanPlay);
            return () => video.removeEventListener('canplay', handleCanPlay);
        }
    }, [desiredVideoTime, currentClipRes]);

    // Add a seeking state handler
    useEffect(() => {
        if (!videoElement.current) return;

        const handleSeeking = () => {
            console.log('Video seeking started');
        };

        const handleSeeked = () => {
            console.log('Video seeking ended, current time:', videoElement.current?.currentTime);
        };

        const video = videoElement.current;
        video.addEventListener('seeking', handleSeeking);
        video.addEventListener('seeked', handleSeeked);

        return () => {
            video.removeEventListener('seeking', handleSeeking);
            video.removeEventListener('seeked', handleSeeked);
        };
    }, []);

    const resolveUrl = useCallback( (path: string) => {
        return getFullUrlFromRelative(path, currentProject);
    }, [currentProject])

    const scrollFinishedDebounced = useMemo<() => void>(() => {
        return debounce(() => {
            dispatch(activeVideoActions.scrollFinished());
        }, 500)
    }, [dispatch]);

    const loadAndDispatchLoc = useCallback(async () => {
        assert(locUrl, 'invalid loc url');
        dispatch(activeVideoActions.startedLoadingLoc());
        const fullLocUrl = resolveUrl(locUrl);
        console.log('loading loc', locUrl);
        const locResponse = await fetch(fullLocUrl);
        const locJson: LocRecord[] = await locResponse.json();
        console.log('loc loaded successfully');
        dispatch(activeVideoActions.locDataLoaded(locJson));
    }, [locUrl, dispatch, resolveUrl]);

    const loadAndDispatchGps = useCallback(async () => {
        assert(gpsUrl, 'invalid gps url');
        dispatch(activeVideoActions.startedLoadingGps());
        console.log('loading gps', gpsUrl);
        const fullGpsUrl = resolveUrl(gpsUrl);
        const locResponse = await fetch(fullGpsUrl);
        const gpsJson: GpsRecord[] = await locResponse.json();
        console.log('GPS loaded successfully');
        dispatch(activeVideoActions.gpsDataLoaded(gpsJson));
    }, [gpsUrl, dispatch, resolveUrl]);

    const loadAndDispatchAssets = useCallback(async () => {
        assert(assetsUrl, 'invalid assets url');
        dispatch(activeVideoActions.startedLoadingAssets());
        console.log('loading assets', assetsUrl);
        const fullGpsUrl = resolveUrl(assetsUrl);
        const assetsResponse = await fetch(fullGpsUrl);
        const assets: AssetRecord[] = await assetsResponse.json();
        console.log('Assets loaded successfully');
        dispatch(activeVideoActions.assetsLoaded(assets));
    }, [assetsUrl, dispatch, resolveUrl]);

    useEffect(() => {
        loadAndDispatchLoc().catch(console.error);
    }, [locUrl, loadAndDispatchLoc]);

    useEffect(() => {
        loadAndDispatchGps().catch(console.error);
    }, [gpsUrl, loadAndDispatchGps]);

    useEffect(() => {
        loadAndDispatchAssets().catch(console.error);
    }, [assetsUrl, loadAndDispatchAssets]);

    if(!currentClipRes || !currentClip)
        return <Navigate to="/" replace={true}/>;

    const fullOverlayUrl = overlayImageRelativePath
        ? resolveUrl(overlayImageRelativePath)
        : null;

    const forceHighRes = window.localStorage.getItem('forceHighResPreview') === '1';
    const previewFrameHref = useHighRes || forceHighRes
        ? currentClip.fullFrameTemplateHref
        : currentClip.previewFrameTemplateHref;

    const currentFramePreviewUrl = formatFrameUrl(previewFrameNumber, previewFrameHref, currentProject);
    const currentVideoUrl = resolveUrl(currentClipRes.href);
    const frameUrlsToCache = framesToCache.map(f => formatFrameUrl(f, currentClip.fullFrameTemplateHref, currentProject));

    if(videoElement.current) {
        if (shouldPlay === true && videoElement.current.paused) {
            console.log('Triggering play');
            videoElement.current?.play().catch(e => {
                // Prevent unhandled promise rejection errors when the video is paused too quickly
            });
        }
        if(shouldPlay === false && !videoElement.current.paused){
            console.log('Triggering pause');
            videoElement.current?.pause();
        }
    }

     // Modified video loaded handler with more debugging
     const videoLoadedHandler = () => {
        if (!videoElement.current) {
            console.log('Video loaded handler called but video element is null');
            return;
        }

        const video = videoElement.current;
        console.log('Video loaded:', {
            duration: video.duration,
            readyState: video.readyState,
            networkState: video.networkState,
            paused: video.paused,
            currentTime: video.currentTime
        });

        if (video.duration === undefined) {
            console.log('Video loaded handler called but duration is undefined');
            return;
        }

        dispatch(activeVideoActions.videoLoaded({
            durationInSeconds: video.duration
        }));
    };

    const timeChanged = () => {
        if(!videoElement.current)
            return;
        let currentTime = videoElement.current.currentTime;
        dispatch(activeVideoActions.videoProgressed(currentTime));
    };

    const previewFrameLoaded = () => {
        dispatch(activeVideoActions.previewFrameLoadComplete());
    }

    const displayNone = { display: "none"};
    const defaultStyle = {};

    function playPause() {
        if (shouldShowRuler || shouldShowOverlayImage || !shouldShowVideo) {
            return;
        }
        dispatch(activeVideoActions.playOrStop())
    }

    function onScroll(e: WheelEvent<HTMLDivElement>) {
        let frameChange = e.deltaY > 0 ? -10 : 10;
        let newFrame = currentFrame + frameChange;
        dispatch(activeVideoActions.previewFrame(newFrame));
        scrollFinishedDebounced();
    }

    function videoFinished() {
        dispatch(activeVideoActions.videoEnded());
    }

    return <div className={styles.VideoPlayer} data-id="outer-video-wrapper" onWheel={onScroll}>
        <video ref={videoElement} className={styles.MainPlayer}
               onPlay={() => dispatch(activeVideoActions.videoPlaybackHasStarted())}
               onPause={() => dispatch(activeVideoActions.videoPlaybackHasStopped())}
               onLoad={videoLoadedHandler}
               onDurationChange={videoLoadedHandler}
               onTimeUpdate={timeChanged}
               style={shouldShowVideo ? defaultStyle : displayNone }
               onClick={playPause}
               src={currentVideoUrl}
               autoPlay={false}
               loop={false}
               onEnded={videoFinished}
               data-id="main-video-element">
        </video>
        { shouldLoadFramePreview &&
            <img className={styles.FramePreviewImage}
                 alt="frame-preview"
                 data-preview-res={useHighRes ? 'high' : 'low'}
                 loading="eager"
                 draggable={false}
                 onLoad={previewFrameLoaded}
                 onError={previewFrameLoaded}
                 style={shouldShowFramePreview ? defaultStyle : displayNone }
                 src={currentFramePreviewUrl}
                 data-id="preview-image" /> }
        { frameUrlsToCache.map(frameUrl =>
            <img
                 key={frameUrl}
                 alt="frame-cache"
                 loading="eager"
                 style={ displayNone }
                 src={frameUrl} />)
         }
        {shouldShowOverlayImage && fullOverlayUrl != null && <img
            alt="overlay"
            loading="eager"
            data-id="overlay-image"
            className={styles.OverlayImage}
            draggable={false}
            src={fullOverlayUrl}/>
        }
        {shouldShowRuler && <RulerOverlay />}
    </div>
};

export default VideoPlayer;
